import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { LoaderWrapper } from '../../components/index';
import { 
  getSubCategoryList, 
  addSubCategory, 
  updateSubCategory, 
  deleteSubCategory,
  getStandardsByField,
  getCategoriesByFieldAndStandard 
} from '../../services/api';

const SubCategoryManagement = () => {
  const [loading, setLoading] = useState(false);
  const [subCategories, setSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [standards, setStandards] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Filter state
  const [filters, setFilters] = useState({
    field: '',
    standard: '',
    category: '',
    displayId: '',
    subCategoryName: '',
  });
  
  // Form state
  const [formData, setFormData] = useState({
    field: '0',
    standard: '',
    categoryId: '',
    displayId: '',
    subCategoryName: '',
  });
  
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Form validation errors
  const [errors, setErrors] = useState({});

  const fieldTypes = [
    { value: '0', label: 'Select Field' },
    { value: 'Testing', label: 'Testing' },
    { value: 'Medical', label: 'Medical' },
    { value: 'Calibration', label: 'Calibration' },
    { value: 'PTP', label: 'PTP' },
    { value: 'RMP', label: 'RMP' },
  ];

  useEffect(() => {
    fetchSubCategories();
  }, []);

  // Apply filters whenever subCategories or filters change
  useEffect(() => {
    applyFilters();
  }, [subCategories, filters]);

  // Fetch standards when field changes
  useEffect(() => {
    if (formData.field && formData.field !== '0') {
      fetchStandards(formData.field);
    } else {
      setStandards([]);
      setFormData(prev => ({ ...prev, standard: '', categoryId: '' }));
      setCategories([]);
    }
  }, [formData.field]);

  // Fetch categories when standard changes
  useEffect(() => {
    if (formData.standard && formData.field && formData.field !== '0') {
      fetchCategories(formData.field, formData.standard);
    } else {
      setCategories([]);
      setFormData(prev => ({ ...prev, categoryId: '' }));
    }
  }, [formData.standard]);

  const fetchSubCategories = async () => {
    try {
      setLoading(true);
      const response = await getSubCategoryList();
      if (response.data.success) {
        setSubCategories(response.data.data || []);
        setFilteredSubCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching sub-categories:', error);
      showSnackbar('Error fetching sub-categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStandards = async (field) => {
    try {
      const response = await getStandardsByField(field);
      if (response.data.success) {
        setStandards(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching standards:', error);
      showSnackbar('Error fetching standards', 'error');
    }
  };

  const fetchCategories = async (field, standard) => {
    try {
      const response = await getCategoriesByFieldAndStandard(field, standard);
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showSnackbar('Error fetching categories', 'error');
    }
  };

  const applyFilters = () => {
    let filtered = [...subCategories];

    // Filter by field
    if (filters.field) {
      filtered = filtered.filter(subCat => 
        subCat.Field === filters.field
      );
    }

    // Filter by standard
    if (filters.standard) {
      filtered = filtered.filter(subCat => 
        subCat.Standard === filters.standard
      );
    }

    // Filter by category
    if (filters.category) {
      filtered = filtered.filter(subCat => 
        subCat.CatName === filters.category
      );
    }

    // Filter by display id
    if (filters.displayId) {
      filtered = filtered.filter(subCat => 
        subCat.SdisId === filters.displayId
      );
    }

    // Filter by sub-category name
    if (filters.subCategoryName) {
      filtered = filtered.filter(subCat => 
        subCat.SubCategory === filters.subCategoryName
      );
    }

    setFilteredSubCategories(filtered);
  };

  const clearFilters = () => {
    setFilters({
      field: '',
      standard: '',
      category: '',
      displayId: '',
      subCategoryName: '',
    });
  };

  // Get unique values for filter dropdowns
  const getUniqueFields = () => {
    return [...new Set(subCategories.map(subCat => subCat.Field).filter(Boolean))];
  };

  const getUniqueStandards = () => {
    let standards = [...new Set(subCategories.map(subCat => subCat.Standard).filter(Boolean))];
    
    if (filters.field) {
      standards = [...new Set(
        subCategories
          .filter(subCat => subCat.Field === filters.field)
          .map(subCat => subCat.Standard)
          .filter(Boolean)
      )];
    }
    
    return standards;
  };

  const getUniqueCategories = () => {
    let categories = [...new Set(subCategories.map(subCat => subCat.CatName).filter(Boolean))];
    
    if (filters.standard) {
      categories = [...new Set(
        subCategories
          .filter(subCat => subCat.Standard === filters.standard)
          .map(subCat => subCat.CatName)
          .filter(Boolean)
      )];
    }
    
    return categories;
  };

  const getUniqueDisplayIds = () => {
    return [...new Set(subCategories.map(subCat => subCat.SdisId).filter(Boolean))];
  };

  const getUniqueSubCategoryNames = () => {
    return [...new Set(subCategories.map(subCat => subCat.SubCategory).filter(Boolean))];
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.field || formData.field === '0') {
      newErrors.field = 'Field Type is required';
    }
    
    if (!formData.standard) {
      newErrors.standard = 'Standard is required';
    }
    
    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }
    
    if (!formData.displayId.trim()) {
      newErrors.displayId = 'Display Id is required';
    }
    
    if (!formData.subCategoryName.trim()) {
      newErrors.subCategoryName = 'Sub-Category Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        sDisplayId: formData.displayId.trim(),
        sdisId: formData.displayId.trim(),
        catId: formData.categoryId,
        field: formData.field,
        standard: formData.standard,
        subCategory: formData.subCategoryName.trim(),
      };
      
      const response = await addSubCategory(payload);
      
      if (response.data.success) {
        showSnackbar('Sub-Category added successfully', 'success');
        resetForm();
        fetchSubCategories();
      } else {
        showSnackbar(response.data.message || 'Error adding sub-category', 'error');
      }
    } catch (error) {
      console.error('Error adding sub-category:', error);
      const errorMessage = error.response?.data?.message || 'Error adding sub-category';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        id: editId,
        sDisplayId: formData.displayId.trim(),
        sdisId: formData.displayId.trim(),
        catId: formData.categoryId,
        field: formData.field,
        standard: formData.standard,
        subCategory: formData.subCategoryName.trim(),
      };
      
      const response = await updateSubCategory(payload);
      
      if (response.data.success) {
        showSnackbar('Sub-Category updated successfully', 'success');
        resetForm();
        fetchSubCategories();
      } else {
        showSnackbar(response.data.message || 'Error updating sub-category', 'error');
      }
    } catch (error) {
      console.error('Error updating sub-category:', error);
      const errorMessage = error.response?.data?.message || 'Error updating sub-category';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (subCategory) => {
    // First set field and wait for standards to load
    setFormData({
      displayId: subCategory.SdisId || '',
      subCategoryName: subCategory.SubCategory || '',
      field: subCategory.Field || '0',
      standard: subCategory.Standard || '',
      categoryId: subCategory.CatId || '',
    });
    
    setEditMode(true);
    setEditId(subCategory.Id || subCategory.id);
    
    // Fetch standards and categories for edit mode
    if (subCategory.Field && subCategory.Field !== '0') {
      await fetchStandards(subCategory.Field);
      if (subCategory.Standard) {
        await fetchCategories(subCategory.Field, subCategory.Standard);
      }
    }
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await deleteSubCategory(deleteId);
      
      if (response.data.success) {
        showSnackbar('Sub-Category deleted successfully', 'success');
        fetchSubCategories();
      } else {
        showSnackbar(response.data.message || 'Error deleting sub-category', 'error');
      }
    } catch (error) {
      console.error('Error deleting sub-category:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting sub-category';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const resetForm = () => {
    setFormData({
      field: '0',
      standard: '',
      categoryId: '',
      displayId: '',
      subCategoryName: '',
    });
    setEditMode(false);
    setEditId(null);
    setErrors({});
    setStandards([]);
    setCategories([]);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return <LoaderWrapper loading={true} type="page" message="Loading..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#00024c' }}>
        {editMode ? 'Edit Sub-Category' : 'Add Sub-Category'}
      </Typography>

      {/* Form Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={editMode ? handleUpdate : handleSubmit}>
          <Grid container spacing={3}>
            {/* Field Type */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={!!errors.field}
                required
              >
                <InputLabel>Field Type</InputLabel>
                <Select
                  name="field"
                  value={formData.field}
                  onChange={handleInputChange}
                  label="Field Type"
                >
                  {fieldTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.field && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.field}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Standard */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={!!errors.standard}
                required
                disabled={!formData.field || formData.field === '0'}
              >
                <InputLabel>Standard</InputLabel>
                <Select
                  name="standard"
                  value={formData.standard}
                  onChange={handleInputChange}
                  label="Standard"
                >
                  <MenuItem value="">
                    <em>Select Standard</em>
                  </MenuItem>
                  {standards.map((std) => (
                    <MenuItem key={std.Id || std.id} value={std.standard || std.Standard}>
                      {std.standard || std.Standard}
                    </MenuItem>
                  ))}
                </Select>
                {errors.standard && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.standard}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Category Name */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={!!errors.categoryId}
                required
                disabled={!formData.standard}
              >
                <InputLabel>Category Name</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleInputChange}
                  label="Category Name"
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.Id || cat.id} value={cat.Id || cat.id}>
                      {cat.CatName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.categoryId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.categoryId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Display Id */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Display Id"
                name="displayId"
                value={formData.displayId}
                onChange={handleInputChange}
                error={!!errors.displayId}
                helperText={errors.displayId}
              />
            </Grid>

            {/* Sub-Category Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Sub-Category Name"
                name="subCategoryName"
                value={formData.subCategoryName}
                onChange={handleInputChange}
                error={!!errors.subCategoryName}
                helperText={errors.subCategoryName}
              />
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color={editMode ? 'error' : 'primary'}
                  disabled={loading}
                >
                  {editMode ? 'Update' : 'Submit'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#e0ebeb',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Sub-Categories List
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSubCategories}
            size="small"
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        {/* Filter Section */}
        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FilterListIcon sx={{ mr: 1, color: '#666' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                  Filter Sub-Categories
                </Typography>
              </Box>
            </Grid>
            
            {/* Field Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={getUniqueFields()}
                value={filters.field || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({
                    ...prev,
                    field: newValue || '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Field" 
                    placeholder="Search field"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
              />
            </Grid>

            {/* Standard Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={getUniqueStandards()}
                value={filters.standard || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({
                    ...prev,
                    standard: newValue || '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Standard" 
                    placeholder="Search standard"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
              />
            </Grid>

            {/* Category Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={getUniqueCategories()}
                value={filters.category || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({
                    ...prev,
                    category: newValue || '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Category" 
                    placeholder="Search category"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
              />
            </Grid>

            {/* Display Id Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={getUniqueDisplayIds()}
                value={filters.displayId || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({
                    ...prev,
                    displayId: newValue || '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Display Id" 
                    placeholder="Search id"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
              />
            </Grid>

            {/* Sub-Category Name Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={getUniqueSubCategoryNames()}
                value={filters.subCategoryName || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({
                    ...prev,
                    subCategoryName: newValue || '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Sub-Category" 
                    placeholder="Search sub-category"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
              />
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12}>
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
                variant="outlined"
                color="secondary"
              >
                Clear All Filters
              </Button>
              <Typography variant="caption" sx={{ ml: 2, color: '#666' }}>
                Showing {filteredSubCategories.length} of {subCategories.length} sub-categories
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Edit</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Delete</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Field</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Standard</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Sub Display Id</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Sub-Category Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSubCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {subCategories.length === 0 ? 'No sub-categories found' : 'No sub-categories match the selected filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubCategories.map((subCategory) => (
                  <TableRow
                    key={subCategory.Id || subCategory.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(subCategory)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(subCategory.Id || subCategory.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">{subCategory.Field || 'N/A'}</TableCell>
                    <TableCell align="center">{subCategory.Standard || 'N/A'}</TableCell>
                    <TableCell align="center">{subCategory.SdisId || 'N/A'}</TableCell>
                    <TableCell align="center">{subCategory.CatName || 'N/A'}</TableCell>
                    <TableCell align="center">{subCategory.SubCategory || 'N/A'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this sub-category?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubCategoryManagement;
